/**
 * Mode Banner Component
 * Top bar indicator for Test and In Progress modes
 */

import { createLogger } from '../core/logger';
import { getOperatingMode, getModeConfiguration } from '../core/config';
import { OperatingMode } from '../types';

const logger = createLogger('ModeBanner');

interface ModeDisplayConfig {
  label: string;
  className: string;
}

const MODE_CONFIGS: Record<Exclude<OperatingMode, 'production'>, ModeDisplayConfig> = {
  test: {
    label: 'Test Mode',
    className: 'mode-banner--test'
  },
  in_progress: {
    label: 'In Progress',
    className: 'mode-banner--in-progress'
  }
};

/**
 * Render the mode banner if applicable
 * Returns the banner element or null if production mode
 */
export function renderModeBanner(): HTMLElement | null {
  // Check if banner already exists
  const existing = document.querySelector('.mode-banner');
  if (existing) {
    logger.debug('Mode banner already exists');
    return existing as HTMLElement;
  }

  const mode = getOperatingMode();
  const modeConfig = getModeConfiguration();

  // No banner in production
  if (mode === 'production' || !modeConfig.showModeBanner) {
    logger.debug('Mode banner not shown (production mode)');
    return null;
  }

  const displayConfig = MODE_CONFIGS[mode];
  if (!displayConfig) {
    logger.warn('Unknown mode for banner', { mode });
    return null;
  }

  logger.info('Rendering mode banner', { mode });

  // Create banner element
  const banner = document.createElement('div');
  banner.className = `mode-banner ${displayConfig.className}`;
  banner.id = 'mode-banner';
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');

  banner.innerHTML = `
    <span class="mode-banner-label">${displayConfig.label}</span>
  `;

  // Add class to body for CSS targeting
  document.body.classList.add('has-mode-banner');

  // Prepend to body (before other content to push it down)
  document.body.prepend(banner);

  return banner;
}

/**
 * Remove the mode banner from DOM
 */
export function removeModeBanner(): void {
  const banner = document.querySelector('.mode-banner');
  if (banner) {
    banner.remove();
    logger.debug('Mode banner removed');
  }
}
